import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './NovaSuccessModal.module.sass';
import { closeUnlessAdminConsumed } from './modalKit';
import BankLogo from '../components/BankLogo';
import {
    X, Send, ArrowRight, Landmark, Check, Copy, User, CreditCard,
    CalendarDays,
} from 'lucide-react';

/**
 * NovaSuccessModal — «чек об отправке» после вывода: тот же дизайн, что
 * у модалки деталей транзакции в брокере (бренд-шапка, маршрут
 * биржа → банк, сумма, перфорация, таймлайн, чековый список), но со своей
 * логикой: статус всегда «успешно» (перевод совершён), дата — из paymentDate,
 * TX ID в режиме отзовика перегенерируется по клику, счёт скрывается глазком.
 *
 * Поведение повторяет классический FintechSuccessModal, меняется только
 * вёрстка. Рендерится порталом внутрь корня .nova
 */

// Копейки мельче и приподняты — стиль дэшборда
const splitAmount = (amount) => {
    const str = String(amount || '');
    let parts = null;
    if (str.includes(',') && str.lastIndexOf(',') > str.lastIndexOf('.')) {
        parts = [str.slice(0, str.lastIndexOf(',')), str.slice(str.lastIndexOf(',') + 1)];
    } else if (str.includes('.')) {
        parts = [str.slice(0, str.lastIndexOf('.')), str.slice(str.lastIndexOf('.') + 1)];
    }
    return parts && parts[1]?.length === 2 ? parts : [str, null];
};

const generateTxId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'TX';
    for (let i = 0; i < 6; i += 1) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

export default function NovaSuccessModal({
    onClose,
    safeT,
    siteSettings = {},
    currency = 'RM',
    amount = '0.00',
    name = '',
    bank = '',
    bankIconSrc,
    accountLabel = 'Счет:',
    accountValue = '',
    initialVisible = true,
    paymentDate = '',
    txId = '',
    onTxIdRegenerate,
}) {
    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const [host, setHost] = useState(null);
    const [isClosing, setIsClosing] = useState(false);

    // В режиме отзовика данные по умолчанию скрыты
    const [isVisible, setIsVisible] = useState(
        siteSettings?.otzovikMode ? false : Boolean(initialVisible),
    );
    const [localTxId, setLocalTxId] = useState(txId);
    const [txIdCopied, setTxIdCopied] = useState(false);

    useEffect(() => {
        setLocalTxId(txId);
    }, [txId]);

    useEffect(() => {
        setHost(document.querySelector('[data-nova-theme]'));
    }, []);

    const handleClose = () => {
        if (isClosing) return;
        setIsClosing(true);
        setTimeout(onClose, 200);
    };

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClosing]);

    if (!host) return null;

    // TX ID: в режиме отзовика клик перегенерирует ID (админ-фишка)
    const handleTxIdClick = () => {
        if (!siteSettings?.otzovikMode) return;
        const newTxId = generateTxId();
        setLocalTxId(newTxId);
        onTxIdRegenerate?.(newTxId);
    };

    const copyTxId = () => {
        try {
            navigator.clipboard?.writeText(localTxId);
            setTxIdCopied(true);
            setTimeout(() => setTxIdCopied(false), 1600);
        } catch (_) { /* клипборд недоступен — молча */ }
    };

    const logoSrc = siteSettings?.siteLogo
        ? (siteSettings.siteLogo.startsWith('/') || siteSettings.siteLogo.startsWith('http')
            ? siteSettings.siteLogo
            : `/logo/${encodeURIComponent(siteSettings.siteLogo)}`)
        : null;

    const maskedAccount = (!accountValue || accountValue.length <= 4)
        ? accountValue
        : `****${accountValue.slice(-4)}`;

    const [intPart, cents] = splitAmount(amount);

    // Перевод совершён: таймлайн пройден целиком, финал — «Отправлено»
    const timelineSteps = [
        t('brokerProgram.timeline.credited', 'Зачислено'),
        t('brokerProgram.timeline.processing', 'В обработке'),
        t('brokerProgram.timeline.sent', 'Отправлено'),
    ];

    return createPortal(
        <div
            className={`${s.overlay} ${isClosing ? s.overlayClosing : ''}`}
            onClick={closeUnlessAdminConsumed(handleClose)}
        >
            <div
                className={`${s.modal} ${isClosing ? s.modalClosing : ''}`}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                {/* ── Бренд-шапка */}
                <div className={s.modalHead}>
                    <div className={s.modalBrand}>
                        {logoSrc && (
                            <img
                                className={s.modalLogo}
                                src={logoSrc}
                                alt=""
                                onError={(e) => {
                                    if (!e.target.src.includes('/data/logo/') && siteSettings?.siteLogo) {
                                        e.target.src = `/data/logo/${encodeURIComponent(siteSettings.siteLogo)}`;
                                    } else {
                                        e.target.style.display = 'none';
                                    }
                                }}
                            />
                        )}
                        <span className={s.modalBrandText}>{siteSettings?.siteName || ''}</span>
                    </div>
                    <button type="button" className={s.modalClose} onClick={handleClose} aria-label="Close">
                        <X size={17} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                </div>

                {/* ── Чек: верх (маршрут + сумма), перфорация, низ (детали) */}
                <div className={s.receipt}>
                    <div className={s.routeBlock}>
                        <div className={s.route}>
                            <span className={s.routeNode}>
                                {logoSrc ? (
                                    <img
                                        src={logoSrc}
                                        alt=""
                                        onError={(e) => {
                                            if (!e.target.src.includes('/data/logo/') && siteSettings?.siteLogo) {
                                                e.target.src = `/data/logo/${encodeURIComponent(siteSettings.siteLogo)}`;
                                            } else {
                                                e.target.style.display = 'none';
                                            }
                                        }}
                                    />
                                ) : (
                                    <Send size={17} strokeWidth={2} aria-hidden="true" />
                                )}
                            </span>
                            <span className={s.routeLine} aria-hidden="true">
                                <span className={s.routeArrow}>
                                    <ArrowRight size={11} strokeWidth={2.6} />
                                </span>
                            </span>
                            <span className={`${s.routeNode} ${s.routeNodeBank}`}>
                                {bankIconSrc ? (
                                    <BankLogo src={bankIconSrc} alt={bank} />
                                ) : (
                                    <Landmark size={17} strokeWidth={2} aria-hidden="true" />
                                )}
                            </span>
                        </div>

                        <span className={s.modalAmountLabel}>
                            {t('modals.success.amountSent', 'Сумма отправлена')}
                        </span>
                        <span className={s.modalAmountValue}>
                            <span className={s.modalCurrency}>{currency}</span>
                            <span className={s.modalAmountNum}>
                                {intPart}
                                {cents !== null && <span className={s.modalCents}>{cents}</span>}
                            </span>
                        </span>

                        <div className={s.modalChips}>
                            <span className={`${s.txStatus} ${s.bGood}`}>
                                {t('brokerProgram.statuses.success', 'Успешно')}
                            </span>
                            {paymentDate && (
                                <span className={s.dateChip}>
                                    <CalendarDays size={11} strokeWidth={2} aria-hidden="true" />
                                    {paymentDate}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Перфорация отрывного чека */}
                    <div className={s.perforation} aria-hidden="true">
                        <i className={s.notchL} />
                        <span className={s.perfLine} />
                        <i className={s.notchR} />
                    </div>

                    {/* Таймлайн: все шаги пройдены — деньги отправлены */}
                    <div className={s.timeline}>
                        {timelineSteps.map((label, i) => (
                            <React.Fragment key={label}>
                                {i > 0 && <span className={`${s.tLine} ${s.tLineDone}`} />}
                                <div className={s.tStep}>
                                    <span className={`${s.tDot} ${s.tDotDone} ${i === timelineSteps.length - 1 ? s.tDotFinal : ''}`}>
                                        <Check size={10} strokeWidth={3} aria-hidden="true" />
                                    </span>
                                    <span className={`${s.tLabel} ${s.tLabelDone}`}>{label}</span>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Чековый список деталей */}
                    <div className={s.modalDetails}>
                        <span className={s.modalDetailsTitle}>
                            {t('modals.success.recipientDetails', 'Данные получателя')}
                        </span>
                        <div className={s.modalDetailRow}>
                            <span className={s.modalDetailLabel}>
                                <span className={s.detailIconPlate}><Copy size={11} strokeWidth={2.1} aria-hidden="true" /></span>
                                TX ID
                            </span>
                            <span className={s.modalDetailValue}>
                                <code
                                    className={`${s.modalMono} ${siteSettings?.otzovikMode ? s.txIdClickable : ''}`}
                                    onClick={handleTxIdClick}
                                >
                                    #{localTxId}
                                </code>
                                <button
                                    type="button"
                                    className={s.eyeBtn}
                                    onClick={copyTxId}
                                    aria-label={t('settings.address.copy', 'Копировать')}
                                >
                                    {txIdCopied
                                        ? <Check size={13} strokeWidth={2.4} className={s.copyDone} aria-hidden="true" />
                                        : <Copy size={13} strokeWidth={2} aria-hidden="true" />}
                                </button>
                            </span>
                        </div>
                        <div className={s.modalDetailRow}>
                            <span className={s.modalDetailLabel}>
                                <span className={s.detailIconPlate}><User size={11} strokeWidth={2.1} aria-hidden="true" /></span>
                                {t('modals.success.userDetails.name', 'Получатель:')}
                            </span>
                            <span className={s.modalDetailValue}>{name}</span>
                        </div>
                        <div className={s.modalDetailRow}>
                            <span className={s.modalDetailLabel}>
                                <span className={s.detailIconPlate}><Landmark size={11} strokeWidth={2.1} aria-hidden="true" /></span>
                                {t('modals.success.userDetails.bank', 'Банк:')}
                            </span>
                            <span className={s.modalDetailValue}>
                                {bank}
                                {bankIconSrc && (
                                    <span className={s.modalBankPlate}>
                                        <BankLogo src={bankIconSrc} alt="" />
                                    </span>
                                )}
                            </span>
                        </div>
                        <div className={s.modalDetailRow}>
                            <span className={s.modalDetailLabel}>
                                <span className={s.detailIconPlate}><CreditCard size={11} strokeWidth={2.1} aria-hidden="true" /></span>
                                {accountLabel}
                            </span>
                            <span className={s.modalDetailValue}>
                                <code className={s.modalMono}>
                                    {isVisible ? accountValue : maskedAccount}
                                </code>
                                <button
                                    type="button"
                                    className={s.eyeBtn}
                                    onClick={() => setIsVisible(!isVisible)}
                                    aria-label={isVisible ? 'Hide' : 'Show'}
                                >
                                    {isVisible ? (
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <path d="M1 12C3 7 7.5 4 12 4s9 3 11 8c-2 5-6.5 8-11 8S3 17 1 12Z" stroke="currentColor" strokeWidth="2" />
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                        </svg>
                                    ) : (
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </button>
                            </span>
                        </div>
                    </div>
                </div>

                <button type="button" className={s.modalCloseBtn} onClick={handleClose}>
                    {t('modals.success.close', 'Закрыть')}
                </button>
            </div>
        </div>,
        host,
    );
}
